import {
  type ChangeEventHandler,
  type DragEventHandler,
  Suspense,
  useCallback,
  useState,
  useTransition,
} from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { AxiosError } from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Dialog } from 'radix-ui';
import { useForm } from 'react-hook-form';
import useSWRMutation from 'swr/mutation';

import { useAccess } from '@quick/auth';
import { Button } from '@quick/components/Button';
import { Modal } from '@quick/components/Modal';
import { Progress } from '@quick/components/Progress';
import { useToast } from '@quick/components/Toaster';
import type { DetailError } from '@quick/go-api-types/common';
import type { PaymentsAndBillingBulkLoad } from '@quick/go-api-types/money/bulk_load';

import { useAppTranslation } from '@/app/i18n/client';
import Massive from '@/assets/massive.svg';
import { InlineSpinner } from '@/components/core/InlineSpinner';
import { RevalidationMessage } from '@/components/massive/RevalidationMessage';
import { LoadBar } from '@/components/operations/Massive/LoadBar';
import { standardError } from '@/layers/core/deal/mappers/standardError';
import { useCanReadWrite } from '@/layers/core/deal/services/useCanReadWrite';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { useModulePermissions } from '@/layers/core/deal/services/useModulePermissions';
import { exportable } from '@/layers/core/helpers/exportable';
import { closeAppModal, openAppModal } from '@/layers/core/helpers/modal';
import { strictMutate } from '@/layers/core/helpers/mutate';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

const MODAL_QUERY_ID = 'pbblm';
const BULK_URL = `/money/payment_billing/bulk-load`;

const closeModal = () => closeAppModal(MODAL_QUERY_ID);

export default function PaymentsAndBillingBulkLoadModal() {
  const { t } = useAppTranslation(['common', 'balance']);
  const p = useModulePermissions();
  const { w: canWrite } = useCanReadWrite(p?.operations?.balances?.balance);
  const searchParams = useSearchParams();
  const queryUuid = searchParams?.get(MODAL_QUERY_ID)?.toString();

  if (!canWrite) return null;

  const trigger = (
    <Dialog.Trigger asChild>
      <Button
        type="button"
        className="flex h-9 items-center justify-center text-sm"
        onClick={() => openAppModal(MODAL_QUERY_ID)}
      >
        {t('balance:paymentsAndBilling.modal.trigger')}
      </Button>
    </Dialog.Trigger>
  );
  const isCreating = queryUuid === 'new';

  return (
    <Modal
      className="z-40"
      contentClassName="!bg-bg"
      isOpen={Boolean(queryUuid)}
      onClose={closeModal}
      trigger={trigger}
    >
      <article className="flex max-w-md flex-col gap-4 overflow-auto">
        <Dialog.Title className="text-center text-lg dark:text-white">
          {t('balance:paymentsAndBilling.modal.bulkTitle')}
        </Dialog.Title>
        <div className="flex justify-center">
          <Massive className="max-h-40" />
        </div>
        <Suspense>
          {isCreating ? <BulkLoadForm /> : <BulkLoadDetails />}
        </Suspense>
      </article>
    </Modal>
  );
}

function BulkLoadDetails() {
  const searchParams = useSearchParams();
  const queryUuid = searchParams?.get(MODAL_QUERY_ID)?.toString();

  const [lastRevalidation, setLastRevalidation] = useState(dayjs());

  const { data, error, isLoading, isValidating } = useFetch<
    PaymentsAndBillingBulkLoad,
    DetailError
  >(
    queryUuid && {
      url: `${BULK_URL}/${queryUuid}`,
      auth: true,
    },
    {
      onSuccess: (data) => {
        setLastRevalidation(dayjs());
        const billedServices = data.data.result.status.billed_services || 0;
        const paidServices = data.data.result.status.paid_services || 0;

        if (
          data.data.result.status.percentage === 100 &&
          billedServices + paidServices > 0
        ) {
          strictMutate('/money/payment_billing_report');
        }
      },
      refreshInterval(latestData) {
        return latestData?.data.result.status.percentage === 100 ? 0 : 5000;
      },
      shouldRetryOnError: (err) =>
        ![400, 500, 503].includes(err?.response?.status ?? 0),
    }
  );

  const percentage = data?.result.status.percentage;
  const isProcessing = typeof percentage === 'number' && percentage < 100;
  const billedServices = data?.result.status.billed_services || 0;
  const paidServices = data?.result.status.paid_services || 0;
  const isFinished = billedServices + paidServices > 0 && percentage === 100;
  const isFinishedWithNothing =
    billedServices + paidServices === 0 &&
    percentage === 100 &&
    !data?.result.status.errors_found;

  if (!queryUuid) return null;
  return (
    <div className="bg-grp flex flex-col gap-4 rounded p-4 shadow">
      {error && (
        <p
          className="w-full max-w-prose bg-red-200 px-4 py-2 font-medium text-red-900 dark:bg-red-900
            dark:text-red-100"
        >
          {standardError(error.response?.data.detail ?? error.message)}
        </p>
      )}
      {data?.result.status.errors_found && (
        <DownloadErrorFile uuid={queryUuid} showSeparator={!!isFinished} />
      )}
      <LoadBar progress={percentage || 0} show={isProcessing} />
      {isLoading && (
        <div className="flex w-full items-center justify-center p-4">
          <InlineSpinner />
        </div>
      )}
      {isFinishedWithNothing && <Empty />}
      {isFinished && <Finished billed={billedServices} paid={paidServices} />}
      {isProcessing && (
        <>
          <Processing validating={isValidating} />
          <RevalidationMessage
            size="sm"
            date={lastRevalidation}
            className="mt-0"
          />
        </>
      )}
    </div>
  );
}

type BulkLoadFormValues = {
  file: '' | File;
  observation_payment: string;
  observation_billing: string;
};

function BulkLoadForm() {
  const {
    t,
    i18n: { language },
  } = useAppTranslation(['common', 'balance', 'massive']);
  const p = useModulePermissions();
  const router = useRouter();
  const { w: canWrite } = useCanReadWrite(p?.operations?.balances?.balance);

  const { toast } = useToast();
  const token = useAccess();

  const [errorUrl, setErrorUrl] = useState<string>();
  const [step, setStep] = useState<1 | 2>(1);

  const {
    handleSubmit,
    setValue,
    register,
    formState: { isSubmitting },
    watch,
  } = useForm<BulkLoadFormValues>({
    defaultValues: {
      file: '',
      observation_payment: '',
      observation_billing: '',
    },
  });

  const onDrop = useCallback<DragEventHandler<HTMLDivElement>>(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file || !/^(.*\.((xls|xlsx|csv)$))$/.test(file.name)) return;
      setValue('file', file, { shouldDirty: true });
    },
    [setValue]
  );

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      e.preventDefault();
      const file = e.target.files?.[0];
      if (!file) return;
      setValue('file', file, { shouldDirty: true });
    },
    [setValue]
  );

  const file = watch('file');
  const config = {
    headers: {
      ...(token && { Authorization: token }),
      'Accept-Language': language,
      'Content-Type': 'multipart/form-data',
    },
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!canWrite) return;
    try {
      setErrorUrl(undefined);
      const res = await axios_demand.postForm<{ uuid: string }>(
        BULK_URL,
        data,
        config
      );
      strictMutate(BULK_URL);
      const params = new URLSearchParams(document.location.search);
      params.set(MODAL_QUERY_ID, res.data.uuid);
      router.push(`${document.location.pathname}?${params.toString()}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response && 'url' in error.response.data) {
          setErrorUrl(error.response?.data?.url);
          setStep(1);
        }
        toast({
          description: t('common:msg.has_ocurred', {
            msg: standardError(
              (error?.response?.data as { detail?: unknown })?.detail ??
                error.message
            ),
          }),
          variant: 'danger',
          duration: 5000,
        });
      } else {
        toast({
          description: t('common:msg.unexpected_error'),
          variant: 'danger',
          duration: 4000,
        });
      }
    }
  });

  if (step === 2) {
    return (
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <div className="bg-grp flex flex-col gap-4 rounded p-4 shadow">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('balance:paymentsAndBilling.modal.observationsTitle')}
          </p>
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 dark:text-gray-300">
              {t('balance:paymentsAndBilling.modal.observationPayment')}
            </label>
            <textarea
              {...register('observation_payment')}
              rows={3}
              placeholder={t(
                'balance:paymentsAndBilling.modal.observationPaymentPlaceholder'
              )}
              className="w-full resize-none rounded-md border border-gray-300 bg-transparent p-2 text-sm
                text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1
                focus:ring-gray-400 dark:border-gray-600 dark:text-gray-200
                dark:placeholder-gray-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 dark:text-gray-300">
              {t('balance:paymentsAndBilling.modal.observationBilling')}
            </label>
            <textarea
              {...register('observation_billing')}
              rows={3}
              placeholder={t(
                'balance:paymentsAndBilling.modal.observationBillingPlaceholder'
              )}
              className="w-full resize-none rounded-md border border-gray-300 bg-transparent p-2 text-sm
                text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1
                focus:ring-gray-400 dark:border-gray-600 dark:text-gray-200
                dark:placeholder-gray-500"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            kind="secondary"
            className="flex flex-1 items-center justify-center"
            onClick={() => setStep(1)}
            disabled={isSubmitting}
          >
            {t('common:previous')}
          </Button>
          <Button
            type="submit"
            className="flex flex-1 flex-row items-center justify-center gap-2"
            disabled={!canWrite || isSubmitting}
          >
            {t('common:confirm')} {isSubmitting && <InlineSpinner />}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form className="flex flex-col gap-4">
      <div className="bg-grp flex flex-col gap-4 rounded p-4 shadow">
        {errorUrl && (
          <>
            {/* oxlint-disable-next-line no-html-link-for-pages */}
            <a
              href={errorUrl}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-red-600 underline dark:text-red-400"
            >
              {t('massive:modal.fileErrorsWithoutCreation')}
            </a>
          </>
        )}
        <div
          className="relative flex h-20 w-full flex-col items-center justify-center rounded-md
            border-2 border-dashed border-gray-300 p-3 px-4 transition duration-300
            dark:border-gray-500 dark:hover:bg-gray-600"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
          }}
          onDrop={onDrop}
        >
          <label>
            <span
              className={
                file
                  ? 'text-gray-500 dark:text-gray-300'
                  : 'text-gray-300 dark:text-gray-500'
              }
            >
              {file ? file.name : t('massive:modal.loadFile')}
            </span>
            <input
              id="pb-massive-file-input"
              className="absolute left-0 top-0 z-10 h-full w-full opacity-0"
              type="file"
              name="massive-file-input"
              value={[]}
              accept=".csv,.xls,.xlsx"
              onChange={onChange}
              disabled={!canWrite}
            />
          </label>
        </div>
        <a
          href="https://quickapp-storage.s3.amazonaws.com/resources/massive_templates/example_massive_payment_billing.xlsx"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-gray-600 underline dark:text-gray-400"
        >
          {t('common:massive.excel')}
        </a>
      </div>
      <Button
        type="button"
        className="flex w-full flex-row items-center justify-center gap-2"
        disabled={!canWrite || !file}
        onClick={() => setStep(2)}
      >
        {t('common:continue')}
      </Button>
    </form>
  );
}

function DownloadErrorFile({
  uuid,
  showSeparator,
}: {
  uuid: string;
  showSeparator: boolean;
}) {
  const { t } = useAppTranslation(['massive', 'common']);
  const { toast } = useToast();
  const token = useAccess();

  const [download, setDownload] = useState<number>();
  const [, startTransition] = useTransition();

  const { trigger, isMutating } = useSWRMutation(
    `${BULK_URL}/${uuid}/errors`,
    async (url) => {
      setDownload(0);
      return await axios_demand.get<Blob>(url, {
        responseType: 'blob',
        data: {},
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: token,
        },
        onDownloadProgress(progressEvent) {
          const size =
            progressEvent.event.srcElement.getResponseHeader('content-length');
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / size
          );
          startTransition(() => {
            setDownload(percentCompleted);
          });
        },
      });
    },
    {
      onSuccess: (response) => {
        setTimeout(() => {
          setDownload(undefined);
        }, 1000);
        exportable(response, `${uuid}.csv`);
      },
      onError: async (err) => {
        setDownload(undefined);
        let msg = JSON.stringify(err);
        if (err instanceof AxiosError) {
          const data: Blob = err.response?.data;
          msg = standardError(JSON.parse(await data.text()));
        }
        toast({
          description: t('common:msg.has_ocurred', { msg }),
          variant: 'danger',
        });
      },
    }
  );

  return (
    <>
      <p className="dark:text-white">
        {t('massive:modal.fileErrorsWithoutCreation')}
      </p>
      <div className="relative w-full">
        <Button
          onClick={() => trigger()}
          disabled={isMutating || !token}
          className={clsx(
            'flex w-full flex-row items-center justify-center gap-2',
            download && 'rounded-b-none'
          )}
          variant="accept"
        >
          {t('common:download')}
        </Button>
        {typeof download === 'number' && (
          <Progress
            value={download}
            className="absolute h-2 rounded-b rounded-t-none"
          />
        )}
      </div>
      {showSeparator && (
        <hr className="border border-gray-300 dark:border-gray-600" />
      )}
    </>
  );
}

function Finished({ billed, paid }: { billed: number; paid: number }) {
  const { t } = useAppTranslation(['balance']);

  return (
    <p
      className="w-full max-w-prose bg-green-200 px-4 py-2 font-medium text-green-900
        dark:bg-green-900 dark:text-green-100"
    >
      {t('balance:paymentsAndBilling.modal.bulkLoadFinished', {
        billed,
        paid,
      })}
    </p>
  );
}

function Empty() {
  const { t } = useAppTranslation(['balance']);

  return (
    <p
      className="w-full max-w-prose bg-yellow-200 px-4 py-2 font-medium text-yellow-900
        dark:bg-yellow-900 dark:text-yellow-100"
    >
      {t('balance:paymentsAndBilling.modal.bulkLoadFinishedEmpty')}
    </p>
  );
}

function Processing({ validating }: { validating: boolean }) {
  const { t } = useAppTranslation(['massive', 'common']);

  return (
    <p
      className={clsx(
        `w-full max-w-prose whitespace-pre bg-blue-200 px-4 py-2 font-medium
        text-blue-900 duration-300 dark:bg-blue-900 dark:text-blue-100`,
        validating ? 'opacity-80' : ''
      )}
    >
      {t('common:massive.processing')}
    </p>
  );
}

export { MODAL_QUERY_ID as PAYMENTS_BILLING_MODAL_QUERY_ID };
